import React from 'react';
import { Box, Flex, Text } from 'rebass';
import { Label } from '../Form';
import { WaterHazardItem } from '../../modes/admin/pages/waterHazards/WaterHazards.query';
import ButtonToggle from '../Button/ButtonToggle';
import UploadDrop from '../File/UploadDrop';
import Form from '../Form/Form';
import FormRow from '../Form/FormRow';
import FormColumn from '../Form/FormColumn';
import { GrowyTextarea } from '@/components/Form/GrowyTextarea';
import DataTable from '../Table/DataTable';
import assembliesColumns from './SurveyTemplateAssemblies.columns';
import { SurveyTemplateInput } from './SurveyTemplateForm';

const SurveyTemplateRow: React.FC<{ children: React.ReactNode }> = (props) => {
  return (
    <FormRow
      gridTemplateColumns="1fr auto"
      px={4}
      mx={-4}
      py={3}
      mt={0}
      sx={{
        ':nth-child(odd)': {
          backgroundColor: 'tableAltRow',
        },
      }}
    >
      {props.children}
    </FormRow>
  );
};

export type HazardSurveyResultProps = {
  template: SurveyTemplateInput;
  hazards: WaterHazardItem[];
  hideName?: boolean;
  readOnly?: boolean;
  waterService?: {
    siteNumber: string | null;
    meterNumber: string | null;
    meterSize?: number | null;
    address: {
      street1: string;
      street2?: string | null;
    };
    backflowAssemblies: any[];
  };
};

export const HazardSurveyResult: React.FC<HazardSurveyResultProps> = ({
  template,
  hazards,
  hideName,
  readOnly,
  waterService,
}) => {
  const selectedHazards = hazards.filter((h) =>
    template.hazardIds.includes(h.id)
  );

  const mockWaterService = {
    siteNumber: '12345',
    meterNumber: 'M-123456',
    meterSize: 1.5,
    address: {
      street1: '123 Main St',
      street2: 'Suite 100',
    },
    backflowAssemblies: [
      {
        id: '1',
        serialNumber: 'SN-12345',
        location: 'North side of building',
        type: 'RP',
        lastTestDate: '2023-01-15',
        model: {
          name: '825Y',
          backflowAssemblyMake: {
            name: 'Febco',
          },
        },
        lastTest: {
          finalResult: 'PASS',
          initialResult: 'PASS',
        },
      },
      {
        id: '2',
        serialNumber: 'SN-67890',
        location: 'Mechanical room',
        type: 'DC',
        lastTestDate: '2023-06-20',
        model: {
          name: '975XL',
          backflowAssemblyMake: {
            name: 'Zurn',
          },
        },
        lastTest: {
          finalResult: 'FAIL',
          initialResult: 'FAIL',
        },
      },
    ],
  };

  const service = waterService || mockWaterService;

  return (
    <Box
      bg="white"
      my={3}
      mx="auto"
      py={4}
      sx={{
        boxShadow: 'feature',
        borderRadius: 4,
        minHeight: '100%',
        width: '100%',
        maxWidth: 800,
      }}
    >
      {!hideName && (
        <Text fontSize={4} py={3} pt={0} textAlign="center" sx={{}}>
          {template.name || 'Hazard Assessment Survey'}
        </Text>
      )}

      <Form mt={2} sx={{ gap: 2 }}>
        <Box
          p={4}
          sx={{
            borderRadius: 'default',
            backgroundColor: 'paneBg',
          }}
        >
          <FormRow gridTemplateColumns="repeat(auto-fit, minmax(100px, auto))">
            <FormColumn>
              <Label fontSize={0}>Service Address</Label>
              <Text fontSize={1}>
                {service.address?.street1}
                {service.address?.street2 ? `, ${service.address.street2}` : ''}
              </Text>
            </FormColumn>
            <FormColumn>
              <Label fontSize={0}>Service #</Label>
              <Text fontSize={1}>{service.siteNumber}</Text>
            </FormColumn>
            <FormColumn>
              <Label fontSize={0}>Meter</Label>
              <Text fontSize={1}>
                {service.meterNumber}{' '}
                {service.meterSize ? `${service.meterSize}"` : ''}
              </Text>
            </FormColumn>
          </FormRow>
        </Box>

        {template.instructions && <Text my={4}>{template.instructions}</Text>}

        {selectedHazards.length === 0 &&
          !template.otherHazardsQuestion &&
          !template.allProtectedQuestion && (
            <Text color="gray.500" fontStyle="italic" my={3} textAlign="center">
              No hazards or extra questions selected yet. Add them to see them
              here.
            </Text>
          )}
        <Box>
          {selectedHazards.map((hazard) => (
            <SurveyTemplateRow key={hazard.id}>
              <FormColumn>
                <Text fontWeight="bold" fontSize={1}>
                  {hazard.name}
                </Text>
                {hazard.description && (
                  <Text fontSize={1} mt={1}>
                    {hazard.description}
                  </Text>
                )}
              </FormColumn>
              <FormColumn sx={{ justifyContent: 'center' }}>
                <ButtonToggle
                  disabled={readOnly}
                  name={`hazard-${hazard.id}`}
                  buttonOptions={[
                    { label: 'Yes', value: 'yes' },
                    { label: 'No', value: 'no' },
                    { label: 'Unsure', value: 'unsure' },
                  ]}
                  onClick={() => {}}
                />
              </FormColumn>
            </SurveyTemplateRow>
          ))}
        </Box>

        {template.otherHazardsQuestion && (
          <FormRow gridTemplateColumns="1fr auto">
            <FormColumn>
              <Text>Are there any other known hazards not listed?</Text>
            </FormColumn>
            <FormColumn sx={{ justifyContent: 'center' }}>
              <ButtonToggle
                disabled={readOnly}
                name="other-hazards"
                buttonOptions={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
                onClick={() => {}}
              />
            </FormColumn>
          </FormRow>
        )}

        <FormRow>
          <FormColumn>
            <Label>Backflow Assemblies</Label>
            {service.backflowAssemblies?.length > 0 ? (
              <Flex maxHeight={350}>
                <DataTable
                  columns={assembliesColumns}
                  items={service.backflowAssemblies}
                />
              </Flex>
            ) : (
              <Text fontSize={1} color="gray.500" fontStyle="italic">
                No backflow assemblies on record for this service.
              </Text>
            )}
          </FormColumn>
        </FormRow>

        {template.allProtectedQuestion && (
          <FormRow gridTemplateColumns="1fr auto">
            <FormColumn>
              <Text>
                Are all known hazards protected by a backflow assembly?
              </Text>
            </FormColumn>
            <FormColumn sx={{ justifyContent: 'center' }}>
              <ButtonToggle
                disabled={readOnly}
                name="all-protected"
                buttonOptions={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
                onClick={() => {}}
              />
            </FormColumn>
          </FormRow>
        )}

        <FormRow>
          <FormColumn>
            <Label>Comments</Label>
            <GrowyTextarea
              disabled={readOnly}
              placeholder="Enter comments"
              variant="textarea"
              maxCharacters={255}
            />
          </FormColumn>
        </FormRow>

        {!readOnly && (
          <FormRow>
            <FormColumn>
              <Label>Photos</Label>
              <UploadDrop
                icon="image"
                dropZone={
                  {
                    getRootProps: (p: any) => p,
                    getInputProps: () => ({ style: { display: 'none' } }),
                    isFocused: false,
                    isDragAccept: false,
                    isDragReject: false,
                  } as any
                }
              />
            </FormColumn>
          </FormRow>
        )}
      </Form>
    </Box>
  );
};

export default HazardSurveyResult;
